"use client";

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { SearchBar } from "../components/ui/search-bar";
import { Tabs } from "../components/ui/tabs";
import { DateRangeFilter } from "../components/ui/date-range-filter";
import { Modal } from "../components/ui/modal";
import { CURRENCY_OPTIONS } from "../constants/CURRENCY_OPTIONS";
import axios from "axios";
import { fetchClients, fetchClientDetails } from "../utils/clientsUtils";

const apiUrl = process.env.REACT_APP_API_URL;

export default function ClientsScreen() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);

  const itemsPerPage = 7;

  const getCurrencySymbol = () => {
    if (!user?.veterinary?.currency) return "$";

    const currencyOption = CURRENCY_OPTIONS.find(
      (option) =>
        option.code.toUpperCase() === user.veterinary.currency.toUpperCase(),
    );

    return currencyOption ? currencyOption.symbol : user.veterinary.currency;
  };

  const loadClients = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const clientsData = await fetchClients(token, apiUrl);
      setClients(clientsData);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setError("Failed to load clients. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadClientDetails = useCallback(
    async (clientId) => {
      if (!token || !clientId) return;

      try {
        const details = await fetchClientDetails(token, apiUrl, clientId);
        setClientDetails(details);
      } catch (error) {
        console.error("Error fetching client details:", error);
        setError("Failed to load client details. Please try again.");
      }
    },
    [token],
  );

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleMessageClick = async (client) => {
    try {
      const recipientId = client.id;

      if (!recipientId) {
        setError("Unable to find client information");
        return;
      }

      const response = await axios.post(
        `${apiUrl}/api/v1/conversations`,
        {
          conversation: {
            recipient_id: recipientId,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const conversationPath = `/chats?conversation_id=${response.data.id}`;
      navigate(conversationPath);
    } catch (err) {
      console.error("Error creating/finding conversation:", err);
      setError("Failed to open conversation. Please try again.");
    }
  };

  const filteredClients = clients.filter((client) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();
    return (
      client.first_name.toLowerCase().includes(searchLower) ||
      client.last_name.toLowerCase().includes(searchLower) ||
      client.email.toLowerCase().includes(searchLower) ||
      client.phone_number.includes(searchQuery)
    );
  });

  // Apply status filters
  const filteredByStatus = filteredClients.filter((client) => {
    if (activeFilter === "All") return true;
    if (activeFilter === "Active") {
      return client.status === "active";
    }
    if (activeFilter === "Inactive") {
      return client.status === "inactive";
    }
    return true;
  });

  // Apply date range filter for first appointment date
  const filteredByDate = filteredByStatus.filter((client) => {
    if (!dateRange.from || !dateRange.to) return true;

    const appointmentDate = new Date(client.first_appointment_date);
    appointmentDate.setHours(0, 0, 0, 0);

    const fromDate = new Date(dateRange.from);
    fromDate.setHours(0, 0, 0, 0);

    const toDate = new Date(dateRange.to);
    toDate.setHours(23, 59, 59, 999);

    return appointmentDate >= fromDate && appointmentDate <= toDate;
  });

  const totalPages = Math.ceil(filteredByDate.length / itemsPerPage);
  const currentClients = filteredByDate.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleRowClick = async (client) => {
    setSelectedClient(client);
    setClientDetails(null);
    setIsDetailModalOpen(true);
    await loadClientDetails(client.id);
  };

  const styles = {
    container: {
      padding: "24px",
      minHeight: "100vh",
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      backgroundColor: "var(--background)",
      color: "var(--text)",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      paddingBottom: "16px",
      borderBottom: "1px solid var(--light-gray)",
    },
    headerTitle: {
      fontSize: "24px",
      fontWeight: "600",
      color: "var(--dark-gray)",
      margin: 0,
    },
    error: {
      padding: "12px",
      marginBottom: "16px",
      backgroundColor: "rgba(255, 0, 60, 0.1)",
      color: "var(--error)",
      borderRadius: "8px",
      fontSize: "14px",
      border: "1px solid rgba(255, 0, 60, 0.2)",
    },
    listSection: {
      backgroundColor: "var(--background)",
      borderRadius: "12px",
      border: "1px solid var(--light-gray)",
      padding: "20px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    sectionTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "var(--dark-gray)",
      margin: "0 0 16px 0",
    },
    filtersRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
      flexWrap: "wrap",
      gap: "12px",
    },
    searchRow: {
      display: "flex",
      gap: "12px",
      alignItems: "center",
      flexWrap: "wrap",
    },
    tableContainer: {
      overflowX: "auto",
      overflow: "visible",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    tableHeader: {
      borderBottom: "1px solid var(--light-gray)",
    },
    tableHeaderCell: {
      padding: "12px 16px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: "500",
      color: "var(--gray)",
      textTransform: "capitalize",
    },
    tableRow: {
      borderBottom: "1px solid var(--light-gray)",
      transition: "background-color 0.2s ease",
    },
    tableCell: {
      padding: "16px",
      verticalAlign: "middle",
    },
    tableCellText: {
      padding: "16px",
      verticalAlign: "middle",
      fontSize: "14px",
      color: "var(--gray)",
    },
    clientCell: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    clientAvatar: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: "var(--primary)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "var(--background)",
      fontWeight: "600",
      fontSize: "14px",
    },
    clientName: {
      fontSize: "14px",
      fontWeight: "500",
      color: "var(--dark-gray)",
    },
    contactInfo: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      color: "var(--gray)",
    },
    contactIcon: {
      width: "14px",
      height: "14px",
      color: "var(--icon)",
    },
    contactLink: {
      fontSize: "12px",
      color: "var(--gray)",
      textDecoration: "none",
      transition: "color 0.2s ease",
    },
    contactLinkHover: {
      color: "var(--primary)",
    },
    messageWrapper: {
      position: "relative",
      display: "inline-flex",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    messageIcon: {
      width: "20px",
      height: "20px",
      color: "var(--icon)",
      transition: "color 0.2s ease",
    },
    messageBadge: {
      position: "absolute",
      top: "-6px",
      right: "-6px",
      width: "16px",
      height: "16px",
      borderRadius: "50%",
      backgroundColor: "var(--error)",
      color: "var(--background)",
      fontSize: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "600",
    },
    loadingMessage: {
      padding: "40px",
      textAlign: "center",
      color: "var(--gray)",
      fontSize: "14px",
    },
    errorMessage: {
      padding: "40px",
      textAlign: "center",
      color: "var(--error)",
      fontSize: "14px",
    },
  };

  if (!token) {
    return (
      <div style={styles.container}>
        <div style={styles.errorMessage}>Please log in to view clients.</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Clients</h1>
      </div>
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.listSection}>
        <h2 style={styles.sectionTitle}>List of Clients</h2>

        <div style={styles.filtersRow}>
          <Tabs
            filters={["All", "Active", "Inactive"]}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />
          <div style={styles.searchRow}>
            <div style={{ width: "250px" }}>
              <SearchBar
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search clients by name, email, or phone"
              />
            </div>
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              placeholder="Filter by dates"
            />
          </div>
        </div>

        {loading ? (
          <div style={styles.loadingMessage}>Loading clients...</div>
        ) : (
          <>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.tableHeaderCell}>Client</th>
                    <th style={styles.tableHeaderCell}>Email</th>
                    <th style={styles.tableHeaderCell}>Phone</th>
                    <th style={styles.tableHeaderCell}>First Appointment</th>
                    <th style={styles.tableHeaderCell}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {currentClients.map((client) => {
                    const initials =
                      `${client.first_name.charAt(0)}${client.last_name.charAt(0)}`.toUpperCase();
                    // You can add logic here to check for unread messages if your API provides this
                    const hasUnreadMessages = false; // Set based on your data
                    const unreadCount = 0; // Set based on your data

                    // Format the first appointment date
                    const firstAppointmentDate = client.first_appointment_date
                      ? new Date(
                          client.first_appointment_date,
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : "N/A";

                    return (
                      <tr
                        key={client.id}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "var(--light-gray)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                        style={{ ...styles.tableRow, cursor: "pointer" }}
                        onClick={() => handleRowClick(client)}
                      >
                        <td style={styles.tableCell}>
                          <div style={styles.clientCell}>
                            <div style={styles.clientAvatar}>{initials}</div>
                            <div>
                              <div style={styles.clientName}>
                                {client.first_name} {client.last_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={styles.tableCellText}>
                          <div style={styles.contactInfo}>
                            <Mail style={styles.contactIcon} />
                            <a
                              href={`mailto:${client.email}`}
                              style={styles.contactLink}
                              onMouseEnter={(e) =>
                                (e.target.style.color = "var(--primary)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.color = "var(--gray)")
                              }
                            >
                              {client.email}
                            </a>
                          </div>
                        </td>
                        <td style={styles.tableCellText}>
                          <div style={styles.contactInfo}>
                            <Phone style={styles.contactIcon} />
                            <a
                              href={`https://wa.me/${client.phone_number}`}
                              style={styles.contactLink}
                              onMouseEnter={(e) =>
                                (e.target.style.color = "var(--primary)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.color = "var(--gray)")
                              }
                            >
                              {client.phone_number}
                            </a>
                          </div>
                        </td>
                        <td style={styles.tableCellText}>
                          {firstAppointmentDate}
                        </td>
                        <td
                          style={styles.tableCell}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMessageClick(client);
                          }}
                        >
                          <div
                            style={styles.messageWrapper}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = "0.7";
                              e.currentTarget.querySelector("svg").style.color =
                                "var(--primary)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = "1";
                              e.currentTarget.querySelector("svg").style.color =
                                "var(--icon)";
                            }}
                            title="Open conversation with client"
                          >
                            <MessageSquare style={styles.messageIcon} />
                            {hasUnreadMessages && unreadCount > 0 && (
                              <span style={styles.messageBadge}>
                                {unreadCount}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {currentClients.length === 0 && (
              <div style={styles.loadingMessage}>
                {searchQuery || dateRange.from || dateRange.to
                  ? "No clients match your filters."
                  : "No clients found."}
              </div>
            )}
            {totalPages > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "16px",
                  padding: "12px 0",
                  color: "var(--gray)",
                }}
              >
                <div style={{ fontSize: "14px", color: "var(--gray)" }}>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filteredByDate.length)}{" "}
                  of {filteredByDate.length} clients
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft style={{ width: "16px", height: "16px" }} />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <Button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        variant={currentPage === page ? "outline" : "ghost"}
                      >
                        {page}
                      </Button>
                    ),
                  )}
                  <Button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight style={{ width: "16px", height: "16px" }} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedClient(null);
          setClientDetails(null);
        }}
        title="Client Details"
        size="lg"
      >
        {clientDetails ? (
          <div>
            {console.log("Rendering client details:", clientDetails)}
            {/* Client Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "24px",
                paddingBottom: "16px",
                borderBottom: "1px solid var(--light-gray)",
              }}
            >
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "var(--primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--background)",
                  fontWeight: "600",
                  fontSize: "20px",
                }}
              >
                {`${selectedClient?.first_name?.charAt(0)}${selectedClient?.last_name?.charAt(0)}`.toUpperCase()}
              </div>
              <div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "var(--dark-gray)",
                  }}
                >
                  {selectedClient?.first_name} {selectedClient?.last_name}
                </div>
                <div style={{ fontSize: "14px", color: "var(--gray)" }}>
                  {selectedClient?.status === "active"
                    ? "Active Client"
                    : "Inactive Client"}
                </div>
              </div>
            </div>

            {/* Client Info Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--gray)",
                    marginBottom: "4px",
                  }}
                >
                  Email
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "var(--dark-gray)",
                  }}
                >
                  <a
                    href={`mailto:${selectedClient.email}`}
                    style={styles.contactLink}
                  >
                    {selectedClient.email}
                  </a>
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--gray)",
                    marginBottom: "4px",
                  }}
                >
                  Phone
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "var(--dark-gray)",
                  }}
                >
                  <a
                    href={`https://wa.me/${selectedClient.phone_number}`}
                    style={styles.contactLink}
                  >
                    {selectedClient.phone_number}
                  </a>
                </div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}></div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--gray)",
                    marginBottom: "4px",
                  }}
                >
                  First Appointment
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "var(--dark-gray)",
                  }}
                >
                  {selectedClient?.first_appointment_date
                    ? new Date(
                        selectedClient.first_appointment_date,
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "N/A"}
                </div>
              </div>
            </div>

            {clientDetails.appointments.length > 0 && (
              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "var(--dark-gray)",
                    marginBottom: "12px",
                  }}
                >
                  Recent Appointments ({clientDetails.appointments.length})
                </div>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {clientDetails.appointments.slice(0, 5).map((appointment) => (
                    <div
                      key={appointment.id}
                      style={{
                        padding: "12px",
                        backgroundColor: "var(--light-gray)",
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "var(--dark-gray)",
                          }}
                        >
                          {appointment.service.name}
                        </div>
                        <div
                          style={{
                            display: "inline-flex",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            backgroundColor:
                              appointment.status === "confirmed"
                                ? "#D1FAE5"
                                : appointment.status === "pending"
                                  ? "#FEF3C7"
                                  : appointment.status === "cancelled"
                                    ? "#FEE2E2"
                                    : "#E5E7EB",
                            color:
                              appointment.status === "confirmed"
                                ? "#059669"
                                : appointment.status === "pending"
                                  ? "#D97706"
                                  : appointment.status === "cancelled"
                                    ? "#DC2626"
                                    : "#4B5563",
                          }}
                        >
                          {appointment.status?.charAt(0).toUpperCase() +
                            appointment.status?.slice(1)}
                        </div>
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--gray)" }}>
                        {appointment.pet.name} •{" "}
                        {new Date(appointment.datetime).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "var(--dark-gray)",
                          marginTop: "8px",
                        }}
                      >
                        {getCurrencySymbol()}
                        {appointment.fee.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <Button
                variant="outline"
                onClick={() => {
                  setIsDetailModalOpen(false);
                  setSelectedClient(null);
                  setClientDetails(null);
                }}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => handleMessageClick(selectedClient)}
              >
                <MessageSquare
                  style={{ width: "16px", height: "16px", marginRight: "8px" }}
                />
                Send Message
              </Button>
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "var(--gray)",
            }}
          >
            Loading client details...
          </div>
        )}
      </Modal>
    </div>
  );
}
